import * as yup from 'yup';
import type { HeaderModel } from '@/redux/services/generatedApi/header';
import convertToValidNumber from '@/components/global/Header/utils/convertToValidNumber';
import {
  nonEmptyString,
  optionalString,
  linkItemSchema,
  languageItemSchema,
} from '@/util/yupSchemas';
import type { NullableOptional } from '@/types/util';

const strictLanguageItemSchema = languageItemSchema.shape({
  code: nonEmptyString().required(),
  name: nonEmptyString().required(),
});

const strictLinkItemSchema = linkItemSchema.shape({
  url: nonEmptyString().required(),
  name: nonEmptyString().required(),
});

const headerModelSchema: yup.ObjectSchema<NullableOptional<HeaderModel>> =
  yup.object({
    logo: yup
      .object({
        imageSrc: optionalString(),
        imageAlt: optionalString(),
        storeName: optionalString(),
        homepageTitle: optionalString(),
        clientCode: optionalString(),
      })
      .concat(linkItemSchema)
      .nullable()
      .optional(),
    apps: yup.array().of(linkItemSchema).nullable().optional(),
    currencies: yup.array().of(linkItemSchema).nullable().optional(),

    shoppingCart: yup
      .object({
        itemCount: yup.number().transform(convertToValidNumber).optional(),
      })
      .concat(linkItemSchema)
      .nullable()
      .optional(),

    accountMenu: yup
      .object({
        firstName: optionalString(),
        lastName: optionalString(),
        loginUrl: optionalString(),
        logoutUrl: optionalString(),
        createAccountUrl: optionalString(),
        menuItems: yup
          .array()
          .ensure()
          .compact((item) => !strictLinkItemSchema.isValidSync(item))
          .of(strictLinkItemSchema),
      })
      .nullable()
      .optional(),

    navigationLinks: yup
      .array()
      .ensure()
      .compact((item) => !strictLinkItemSchema.isValidSync(item))
      .of(strictLinkItemSchema)
      .optional(),

    languages: yup
      .array()
      .ensure()
      .compact((item) => !strictLanguageItemSchema.isValidSync(item))
      .of(strictLanguageItemSchema)
      .optional(),

    searchUrl: optionalString(),
  });

const parseHeaderModel = (
  jsonString: string | undefined | null,
): NullableOptional<HeaderModel> | null => {
  if (!jsonString || jsonString.trim() === '') {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(jsonString);
    return headerModelSchema.validateSync(parsed);
  } catch (error) {
    console.warn('HeaderModel validation failed:', error);
    return null;
  }
};

export default parseHeaderModel;
